
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface UserCredits {
  total: number;
  used: number;
  remaining: number;
  refreshDate: string;
  isAdmin: boolean;
}

export const useUserCredits = (userId: string | null) => {
  // Regular users get 5 credits per month
  const REGULAR_USER_CREDITS = 5;
  // Admins get unlimited credits (represented by a high number)
  const ADMIN_CREDITS = 999999;
  
  const [credits, setCredits] = useState<UserCredits>({
    total: REGULAR_USER_CREDITS,
    used: 0,
    remaining: REGULAR_USER_CREDITS,
    refreshDate: getNextMonthDate(),
    isAdmin: false
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (userId) {
      loadUserCredits();
    }
  }, [userId]);

  function getNextMonthDate() {
    const now = new Date();
    // Get the first day of next month
    return new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString();
  }

  const loadUserCredits = async () => {
    setIsLoading(true);
    try {
      // Check if user is admin
      const { data: isAdmin, error: adminError } = await supabase.rpc("is_admin");
      
      if (adminError) {
        console.error("Error checking admin status:", adminError);
      }
      
      // Get the current month start and end dates
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      
      // For regular users, query usage from the database
      // For admins, set unlimited credits
      if (isAdmin) {
        setCredits({
          total: ADMIN_CREDITS,
          used: 0,
          remaining: ADMIN_CREDITS,
          refreshDate: getNextMonthDate(),
          isAdmin: true
        });
      } else {
        // For non-admin users, check actual usage
        const { data: usageData, error: usageError } = await supabase
          .from('prompt_copies')
          .select('id', { count: 'exact' })
          .eq('user_id', userId)
          .gte('created_at', monthStart.toISOString());
        
        if (usageError) {
          console.error("Error loading user credits usage:", usageError);
        }
        
        const used = usageData ? usageData.length : 0;
        setCredits({
          total: REGULAR_USER_CREDITS,
          used: used,
          remaining: Math.max(0, REGULAR_USER_CREDITS - used),
          refreshDate: getNextMonthDate(),
          isAdmin: false
        });
      }
    } catch (error) {
      console.error("Error loading user credits:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // This function would be called when a user sends a message
  const useCredit = async () => {
    if (!userId) return false;
    if (credits.isAdmin) return true; // Admins can always use credits
    if (credits.remaining <= 0) return false; // Regular users are limited

    try {
      // Update local state first (optimistic update)
      setCredits(prev => ({
        ...prev,
        used: prev.used + 1,
        remaining: Math.max(0, prev.remaining - 1)
      }));

      // Record the credit usage in the database for regular users
      const { error } = await supabase
        .from('prompt_copies')
        .insert({
          prompt_id: '00000000-0000-0000-0000-000000000000', // placeholder for chat usage
          user_id: userId,
          counted_in_total: true
        });
        
      if (error) {
        console.error("Error recording credit usage:", error);
      }
      
      return true;
    } catch (error) {
      console.error("Error using credit:", error);
      
      // Revert the optimistic update on error
      setCredits(prev => ({
        ...prev,
        used: prev.used - 1,
        remaining: prev.remaining + 1
      }));
      
      return false;
    }
  };

  return {
    credits,
    isLoading,
    useCredit
  };
};
